import React from 'react';
import { Link } from 'react-router-dom';
import { useSite } from '../content/SiteContext';
import { PageMeta, PageHero } from '../components/ui';

// NOTE: These texts describe how the website currently works. They must be
// reviewed by the business (and legal counsel) before being relied on.
const UPDATED = '15 October 2026';

const DOCS = {
  privacy: {
    path: '/privacy-policy',
    name: 'Privacy policy',
    title: 'Privacy Policy | Quick Way',
    description: 'How the Quick Way website handles your personal information, analytics cookies and messages sent through WhatsApp.',
    body: (B) => (
      <>
        <h2>Who we are</h2>
        <p>This website is operated by Quick Way, a mobile car wash service in Abu Dhabi and Dubai. Contact us at <a href={`mailto:${B.email}`}>{B.email}</a> or {B.phoneDisplay}.</p>
        <h2>Information you send us</h2>
        <p>Our booking, contact, fleet and partner forms do not send your details to a Quick Way server. When you submit a form, your details are placed into a WhatsApp message on your device. We only receive them if you press send in WhatsApp. Messages sent through WhatsApp are also subject to WhatsApp's own privacy policy.</p>
        <p>Fleet quote and partner application details are also saved in your own browser's local storage on the device you used. You can remove them by clearing your browser's site data.</p>
        <p>We use the information you send us (such as your name, mobile number, location and vehicle details) to respond to you, arrange and carry out your car wash, and contact you about your booking.</p>
        <h2>Analytics</h2>
        <p>We use Google Analytics to understand how visitors use this website, for example which pages are viewed and which buttons are clicked. Google Analytics uses cookies and similar technologies. You can block these cookies in your browser settings or use Google's opt-out browser add-on.</p>
        <h2>The Quick Way app</h2>
        <p>If you book through the Quick Way app, the app's own privacy information applies to data collected in the app.</p>
        <h2>Your choices</h2>
        <p>You can ask us what information we hold about you from your messages, or ask us to delete it, by contacting us using the details above.</p>
      </>
    )
  },
  terms: {
    path: '/terms',
    name: 'Terms and conditions',
    title: 'Terms and Conditions | Quick Way',
    description: 'Terms for booking a Quick Way mobile car wash through this website, WhatsApp or phone.',
    body: (B) => (
      <>
        <h2>Bookings</h2>
        <p>Submitting a booking form on this website is a request, not a confirmed booking. A booking is confirmed only when our team confirms the time slot and price with you on WhatsApp, by phone or in the app.</p>
        <h2>Prices</h2>
        <p>Package and extra prices are shown on our <Link to="/pricing">pricing page</Link> in UAE dirhams. For large vehicles, heavily soiled vehicles or special requests, the final price, including any applicable VAT, is confirmed with you before the wash starts.</p>
        <h2>Access to your vehicle</h2>
        <p>Please make sure our team can reach your vehicle at the agreed time and location, and that washing is permitted there. Some buildings and communities require approval for car washing in their car parks. Please share any access instructions when you book.</p>
        <h2>Changes and cancellations</h2>
        <p>See our <Link to="/cancellation-policy">cancellation and refund policy</Link>.</p>
        <h2>Contact</h2>
        <p>Questions about these terms: <a href={`mailto:${B.email}`}>{B.email}</a>.</p>
      </>
    )
  },
  cancellation: {
    path: '/cancellation-policy',
    name: 'Cancellation and refund policy',
    title: 'Cancellation & Refund Policy | Quick Way',
    description: 'How to reschedule or cancel a Quick Way mobile car wash booking and how refund requests are handled.',
    body: (B) => (
      <>
        <h2>Rescheduling and cancelling</h2>
        <p>You can reschedule or cancel a booking at no charge up to 2 hours before your appointment. Message us on WhatsApp at {B.phoneDisplay}, or use the app if you booked there.</p>
        <h2>If we need to reschedule</h2>
        <p>If weather or another issue prevents us from carrying out your wash, we will contact you to agree a new time.</p>
        <h2>Refunds</h2>
        <p>If you paid in advance and need a refund, contact us on WhatsApp or at <a href={`mailto:${B.email}`}>{B.email}</a> with your booking details and our team will review your request.</p>
        <h2>Monthly plans</h2>
        <p>Terms for changing or cancelling a monthly plan are confirmed with you when you subscribe.</p>
      </>
    )
  }
};

const Legal = ({ doc }) => {
  const { BUSINESS } = useSite();
  const d = DOCS[doc];
  return (
    <>
      <PageMeta title={d.title} description={d.description} path={d.path} crumbs={[{ name: d.name, path: d.path }]} noindex />
      <PageHero title={d.name} compact>
        <p>Last updated: {UPDATED}</p>
      </PageHero>
      <section className="section">
        <div className="container container--narrow prose">{d.body(BUSINESS)}</div>
      </section>
    </>
  );
};

export default Legal;
